import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';

import { ROUTE_FEATURE_FLAGS } from '@/config/route-feature-flags';

import { useFeatureFlags } from './use-feature-flags';
import { FEATURE_FLAG_DEFAULTS } from '../constants';

/**
 * Hook for determining which routes have their features enabled in Gaia.
 *
 * This is the single source of truth for route availability during migration.
 * Uses ROUTE_FEATURE_FLAGS to map routes to their controlling feature flags.
 */
export const useMigrationNavigation = () => {
  const flags = useFeatureFlags();
  const ldClient = useLDClient();

  /**
   * Get feature enabled statuses for routes with migration flags.
   *
   * Returns a map of route IDs to their feature flag values:
   * - true = feature is enabled in Gaia (stay in Gaia)
   * - false = feature is not enabled (redirect to Darkwing)
   *
   * Only routes with entries in ROUTE_FEATURE_FLAGS are included.
   * Routes NOT in this map default to Gaia (unless in PENDING_MIGRATION_FROM_DARKWING_ROUTES).
   */
  const getFeatureEnabledStatuses = useCallback(() => {
    const statuses: Record<string, boolean> = {};

    for (const [routeId, flagKey] of Object.entries(ROUTE_FEATURE_FLAGS)) {
      // Use flags if available, otherwise check if ldClient can provide the value
      // If neither are available, use the default from FEATURE_FLAG_DEFAULTS
      if (flags) {
        statuses[routeId] = (flags as any)[flagKey] ?? false;
      } else if (ldClient) {
        // Try to get value directly from ldClient
        const defaultValue = (FEATURE_FLAG_DEFAULTS as any)[flagKey] ?? false;
        statuses[routeId] = ldClient.variation(flagKey, defaultValue);
      } else {
        // Use default value when nothing is available
        statuses[routeId] = (FEATURE_FLAG_DEFAULTS as any)[flagKey] ?? false;
      }
    }

    return statuses;
  }, [flags, ldClient]);

  return {
    getFeatureEnabledStatuses,
  };
};
